import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { ArrowLeft, MessageSquare, Pencil, Plus, Star, Trash2 } from 'lucide-react';
import { BizwebEntityType, ReviewStatus, type ReviewToken } from '@sleed/bizcommerce-api';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Skeleton } from '@/components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { FormField } from '~/components/system/form-field';
import { serverMessage } from '~/lib/api/server-error';
import { isProductTarget, reviewTargetName, reviewTargetTypeLabel } from '~/lib/reviews/reviews';
import { useContributorReviews, useContributorReviewMutations } from '~/hooks/use-contributor-subviews';

interface Props {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	contributorId: number | null;
	contributorName?: string;
}

/**
 * A new editorial review, as on the Reviews page: accepted (the shop's own content) and unrated — a 0 would count in
 * the product's average rating.
 */
const EMPTY: ReviewToken = {
	to_ent: BizwebEntityType.Product,
	tl: '',
	reviewer: '',
	source: '',
	review: '',
	rating: null,
	st: ReviewStatus.Accepted,
	isFeatured: false,
	iv: true,
	si: 0
};

const STATUS_BADGE: Record<number, string> = {
	[ReviewStatus.Pending]: 'bg-warning-subtle text-warning-fg',
	[ReviewStatus.Accepted]: 'bg-success-subtle text-success-fg',
	[ReviewStatus.Declined]: 'bg-danger-subtle text-danger-fg'
};

/** Per-contributor reviews sub-grid (legacy ContributorsReviews popup). List + inline add/edit. */
export function ContributorReviewsDialog({ open, onOpenChange, contributorId, contributorName }: Props) {
	const { t } = useTranslation();
	const { data, isLoading } = useContributorReviews(open ? contributorId : null);
	const { create, update, remove } = useContributorReviewMutations(contributorId ?? 0);
	const [editing, setEditing] = useState<ReviewToken | null>(null);

	useEffect(() => {
		if (!open) setEditing(null);
	}, [open]);

	const set = (patch: Partial<ReviewToken>) => setEditing(prev => ({ ...(prev ?? EMPTY), ...patch }));

	const statusLabel = (st?: number) =>
		st === ReviewStatus.Accepted
			? t('contributors.list.subviews.reviews.statusAccepted')
			: st === ReviewStatus.Declined
				? t('contributors.list.subviews.reviews.statusDeclined')
				: t('contributors.list.subviews.reviews.statusPending');

	const save = async () => {
		if (!editing) return;
		if (!editing.tl?.trim() || !editing.to_id) {
			toast.error(t('validation.required'));
			return;
		}
		try {
			if (editing.id) await update.mutateAsync(editing);
			else await create.mutateAsync(editing);
			toast.success(t('common.save'));
			setEditing(null);
		} catch (err) {
			toast.error(serverMessage(err));
		}
	};

	const del = (r: ReviewToken) => {
		if (!r.id) return;
		remove.mutate(r.id, { onSuccess: () => toast.success(t('common.delete')), onError: e => toast.error(serverMessage(e)) });
	};

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="max-w-[95vw] sm:max-w-3xl">
				<DialogHeader>
					<DialogTitle>{t('contributors.list.subviews.reviews.title')}</DialogTitle>
					<DialogDescription>{contributorName}</DialogDescription>
				</DialogHeader>

				{editing ? (
					<div className="flex max-h-[70vh] flex-col gap-3 overflow-auto">
						<div className="grid grid-cols-2 gap-3">
							{isProductTarget(editing.to_ent) ? (
								<FormField label={t('contributors.list.subviews.reviews.productId')}>
									<Input
										type="number"
										value={editing.to_id ?? ''}
										onChange={e => set({ to_id: e.target.value === '' ? undefined : Number(e.target.value) })}
									/>
								</FormField>
							) : (
								// Only a product target is picked here; a review of another kind of item keeps its target.
								<FormField label={t('contributors.list.subviews.reviews.target')}>
									<div className="flex h-9 items-center truncate rounded-md border border-border bg-elevated px-3 text-sm text-foreground">
										{reviewTargetTypeLabel(t, editing.to_ent, editing.to_ent_nm)} · {reviewTargetName(editing.to_title, editing.to_id)}
									</div>
								</FormField>
							)}
							<FormField label={t('contributors.list.subviews.reviews.reviewTitle')}>
								<Input value={editing.tl ?? ''} onChange={e => set({ tl: e.target.value })} maxLength={50} autoFocus />
							</FormField>
							<FormField label={t('contributors.list.subviews.reviews.reviewer')}>
								<Input value={editing.reviewer ?? ''} onChange={e => set({ reviewer: e.target.value })} maxLength={300} />
							</FormField>
							<FormField label={t('contributors.list.subviews.reviews.source')}>
								<Input value={editing.source ?? ''} onChange={e => set({ source: e.target.value })} maxLength={300} />
							</FormField>
							<FormField label={t('contributors.list.subviews.reviews.rating')}>
								<Input
									type="number"
									min={0}
									max={5}
									step={0.5}
									value={editing.rating ?? ''}
									onChange={e => set({ rating: e.target.value === '' ? null : Number(e.target.value) })}
								/>
							</FormField>
							<FormField label={t('contributors.list.subviews.reviews.status')}>
								<Select value={String(editing.st ?? ReviewStatus.Accepted)} onValueChange={v => set({ st: Number(v) as ReviewStatus })}>
									<SelectTrigger className="w-full" aria-label={t('contributors.list.subviews.reviews.status')}>
										<SelectValue />
									</SelectTrigger>
									<SelectContent>
										<SelectItem value={String(ReviewStatus.Pending)}>{statusLabel(ReviewStatus.Pending)}</SelectItem>
										<SelectItem value={String(ReviewStatus.Accepted)}>{statusLabel(ReviewStatus.Accepted)}</SelectItem>
										<SelectItem value={String(ReviewStatus.Declined)}>{statusLabel(ReviewStatus.Declined)}</SelectItem>
									</SelectContent>
								</Select>
							</FormField>
						</div>
						<FormField label={t('contributors.list.subviews.reviews.review')}>
							<Textarea value={editing.review ?? ''} onChange={e => set({ review: e.target.value })} rows={4} />
						</FormField>
						<div className="grid grid-cols-2 gap-3">
							<div className="flex items-center justify-between rounded-md border border-border px-3 py-2">
								<Label htmlFor="rv-feat">{t('contributors.list.subviews.reviews.featured')}</Label>
								<Switch id="rv-feat" checked={Boolean(editing.isFeatured)} onCheckedChange={next => set({ isFeatured: next })} />
							</div>
							<div className="flex items-center justify-between rounded-md border border-border px-3 py-2">
								<Label htmlFor="rv-iv">{t('contributors.list.subviews.reviews.visible')}</Label>
								<Switch id="rv-iv" checked={Boolean(editing.iv)} onCheckedChange={next => set({ iv: next })} />
							</div>
						</div>
						<div className="flex justify-between">
							<Button variant="ghost" onClick={() => setEditing(null)}>
								<ArrowLeft size={15} /> {t('common.cancel')}
							</Button>
							<Button onClick={save} disabled={create.isPending || update.isPending}>
								{t('common.save')}
							</Button>
						</div>
					</div>
				) : (
					<div className="flex flex-col gap-3">
						<div className="flex justify-end">
							<Button size="sm" onClick={() => setEditing({ ...EMPTY })}>
								<Plus size={15} /> {t('contributors.list.subviews.reviews.add')}
							</Button>
						</div>
						<div className="max-h-[55vh] overflow-auto rounded-md border border-border">
							{isLoading ? (
								<div className="flex flex-col gap-2 p-3">
									{Array.from({ length: 4 }).map((_, i) => (
										<Skeleton key={i} className="h-9 w-full rounded-md" />
									))}
								</div>
							) : !data || data.length === 0 ? (
								<div className="flex flex-col items-center gap-3 py-12 text-center">
									<MessageSquare size={32} className="text-foreground-disabled" strokeWidth={1.25} />
									<p className="text-sm text-foreground-muted">{t('common.noResults')}</p>
								</div>
							) : (
								<table className="w-full border-collapse text-sm">
									<thead className="bg-elevated text-xs text-foreground-muted">
										<tr className="border-b border-border">
											<th className="px-3 py-2 text-left font-medium">{t('contributors.list.subviews.reviews.target')}</th>
											<th className="px-3 py-2 text-left font-medium">{t('contributors.list.subviews.reviews.reviewTitle')}</th>
											<th className="px-3 py-2 text-center font-medium">{t('contributors.list.subviews.reviews.rating')}</th>
											<th className="px-3 py-2 text-left font-medium">{t('contributors.list.subviews.reviews.status')}</th>
											<th className="px-2 py-2" style={{ width: 80 }} />
										</tr>
									</thead>
									<tbody>
										{data.map(r => (
											<tr key={r.id} className="border-b border-border/60 hover:bg-muted/50">
												<td className="px-3 py-2 text-foreground-muted">
													{!isProductTarget(r.to_ent) && (
														<span className="mr-1 text-[11px] text-foreground-subtle">
															{reviewTargetTypeLabel(t, r.to_ent, r.to_ent_nm)} ·
														</span>
													)}
													{r.to_title || r.to_code || reviewTargetName(null, r.to_id)}
												</td>
												<td className="px-3 py-2 text-foreground">{r.tl || '—'}</td>
												<td className="px-3 py-2 text-center">
													<span className="inline-flex items-center gap-1 tabular-nums text-foreground-muted">
														<Star size={13} className="text-warning-fg" /> {r.rating ?? '—'}
													</span>
												</td>
												<td className="px-3 py-2">
													<span className={`rounded-full px-2 py-0.5 text-xs ${STATUS_BADGE[r.st ?? 0] ?? ''}`}>
														{r.st_nm || statusLabel(r.st)}
													</span>
												</td>
												<td className="px-2 py-1 text-center whitespace-nowrap">
													<Button
														variant="ghost"
														size="icon"
														className="h-7 w-7"
														aria-label={t('common.edit')}
														onClick={() => setEditing(r)}>
														<Pencil size={14} />
													</Button>
													<Button
														variant="ghost"
														size="icon"
														className="h-7 w-7 text-danger-fg"
														aria-label={t('common.delete')}
														onClick={() => del(r)}>
														<Trash2 size={14} />
													</Button>
												</td>
											</tr>
										))}
									</tbody>
								</table>
							)}
						</div>
					</div>
				)}
			</DialogContent>
		</Dialog>
	);
}
